import { Howl } from 'howler';

import storeProvider from '../storeProvider';

import {
  setBackendAction,
  setOnlineUsers,
} from '../actions/App';
import { updateFileFragment } from '../actions/Files';
import { addNotification } from '../actions/Notifications';

import { getCurrentUser } from './Auth';
import Console from './Console';
import { getIntl } from './IntlInstance';
import { getUserSetting } from './Settings';

class Badger {
  constructor(options) {
    Object.assign(
      this,
      {
        backgroundColor: '#f00',
        color: '#fff',
        size: 0.6, // 0..1 (Scale in respect to the favicon image size)
        position: 'ne', // Position inside favicon "n", "e", "s", "w", "ne", "nw", "se", "sw"
        radius: 9, // Border radius
        src: '', // Favicon source (dafaults to the <link> icon href)
        onChange() {},
      },
      options,
    );
    this.canvas = document.createElement('canvas');
    this.src = this.src || this.faviconEL.getAttribute('href');
    this.ctx = this.canvas.getContext('2d');
  }

  faviconEL = document.querySelector('link[rel="icon"]');

  drawIcon() {
    this.ctx.clearRect(0, 0, this.faviconSize, this.faviconSize);
    this.ctx.drawImage(this.img, 0, 0, this.faviconSize, this.faviconSize);
  }

  drawShape() {
    const r = this.radius;
    const xa = this.offset.x;
    const ya = this.offset.y;
    const xb = this.offset.x + this.badgeSize;
    const yb = this.offset.y + this.badgeSize;
    this.ctx.beginPath();
    this.ctx.moveTo(xb - r, ya);
    this.ctx.quadraticCurveTo(xb, ya, xb, ya + r);
    this.ctx.lineTo(xb, yb - r);
    this.ctx.quadraticCurveTo(xb, yb, xb - r, yb);
    this.ctx.lineTo(xa + r, yb);
    this.ctx.quadraticCurveTo(xa, yb, xa, yb - r);
    this.ctx.lineTo(xa, ya + r);
    this.ctx.quadraticCurveTo(xa, ya, xa + r, ya);
    this.ctx.fillStyle = this.backgroundColor;
    this.ctx.fill();
    this.ctx.closePath();
  }

  drawVal() {
    const margin = (this.badgeSize * 0.18) / 2;
    this.ctx.beginPath();
    this.ctx.textBaseline = 'middle';
    this.ctx.textAlign = 'center';
    this.ctx.font = `bold ${this.badgeSize * 0.82}px Arial`;
    this.ctx.fillStyle = this.color;
    this.ctx.fillText(
      this.value,
      this.badgeSize / 2 + this.offset.x,
      this.badgeSize / 2 + this.offset.y + margin,
    );
    this.ctx.closePath();
  }

  drawFavicon() {
    this.faviconEL.setAttribute('href', this.dataURL);
  }

  draw() {
    this.drawIcon();
    if (this.value && this.offset) this.drawShape();
    if (this.value && this.offset) this.drawVal();
    this.drawFavicon();
  }

  setup() {
    this.faviconSize = this.img.naturalWidth;
    this.badgeSize = this.faviconSize * this.size;
    this.canvas.width = this.faviconSize;
    this.canvas.height = this.faviconSize;
    const sd = this.faviconSize - this.badgeSize;
    const sd2 = sd / 2;
    this.offset = {
      n: { x: sd2, y: 0 },
      e: { x: sd, y: sd2 },
      s: { x: sd2, y: sd },
      w: { x: 0, y: sd2 },
      nw: { x: 0, y: 0 },
      ne: { x: sd, y: 0 },
      sw: { x: 0, y: sd },
      se: { x: sd, y: sd },
    }[this.position];
  }

  update() {
    this.valuep = Math.min(99, parseInt(this.valuep));
    if (this.img) {
      this.draw();
      if (this.onChange) this.onChange.call(this);
    } else {
      this.img = new Image();
      this.img.addEventListener('load', () => {
        this.setup();
        this.draw();
        if (this.onChange) this.onChange.call(this);
      });
      this.img.src = this.src;
    }
  }

  get dataURL() {
    return this.canvas.toDataURL();
  }

  get value() {
    return this.valuep;
  }

  set value(val) {
    this.valuep = val;
    this.update();
  }
}

const playNotificationSound = () => {
  if (!window.sp.notificationSound) {
    window.sp.notificationSound = new Howl({
      src: ['/sounds/notification-v2.mp3'],
    });
  }
  if (getUserSetting('notification_sound')) {
    window.sp.notificationSound.play();
  }
};

export const getNotificationDataText = (notificationType, data) => {
  const intl = getIntl();

  let dataText = '';
  const dataTextParts = [];

  switch (notificationType) {
    case 'CollectionFilesAdded': {
      if (data) {
        if (data.files) {
          dataTextParts.push(intl.formatMessage({
            defaultMessage: '{count} {count, plural, one {file} other {files}}',
          }, { count: data.files }));
        }
        if (data.link_cards) {
          dataTextParts.push(intl.formatMessage({
            defaultMessage: '{count} link {count, plural, one {card} other {cards}}',
          }, { count: data.link_cards }));
        }
      }
      if (dataTextParts.length === 0) {
        dataText = intl.formatMessage({ defaultMessage: 'files' });
      } else {
        dataText = dataTextParts.join(intl.formatMessage({ defaultMessage: ' and ' }));
      }
      break;
    }

    case 'CommentPosted':
    case 'ShareCommentPosted': {
      dataText = (data.type === 'reply') ? (
        intl.formatMessage({ defaultMessage: 'has replied to your comment' })
      ) : (
        intl.formatMessage({ defaultMessage: 'has commented' })
      );
      break;
    }

    case 'CommentMentioned':
    case 'ShareCommentMentioned': {
      dataText = intl.formatMessage({ defaultMessage: 'has mentioned you in a comment' });
      break;
    }

    default:
      break;
  }

  return dataText;
};

const getBrowserNotificationText = (notification) => {
  const intl = getIntl();

  const {
    type,
    object,
    user,
    data,
  } = notification;

  switch (type) {
    case 'FileFavorited':
    case 'ShareFileFavorited':
      return intl.formatMessage({
        defaultMessage: '{name} favorited {display_name}',
      }, { name: user.name, display_name: object.display_name });

    case 'FileNewVersionUploaded':
      return intl.formatMessage({
        defaultMessage: '{name} added new version of a file {display_name}',
      }, { name: user.name, display_name: object.display_name });

    case 'CollectionFilesAdded':
      return intl.formatMessage({
        defaultMessage: '{name} added {data} to Collection {collection}',
      }, { name: user.name, data: getNotificationDataText(type, data), collection: object.name });

    case 'CollectionAccessGranted':
      return intl.formatMessage({
        defaultMessage: '{name} invited you to collaborate on a Collection {collection}',
      }, { name: user.name, collection: object.name });

    case 'CommentPosted':
    case 'ShareCommentPosted': {
      let name = user.uuid ? user.name : null;
      if (!name) {
        name = data.user_name;
      }
      if (!name) {
        name = intl.formatMessage({ defaultMessage: 'Someone' });
      }
      return intl.formatMessage({
        defaultMessage: '{name} {data} on {display_name}',
      }, {
        name,
        data: getNotificationDataText(type, data),
        display_name: object.display_name,
      });
    }

    case 'CommentMentioned':
    case 'ShareCommentMentioned': {
      let name = user.uuid ? user.name : null;
      if (!name) {
        name = data.user_name;
      }
      if (!name) {
        name = intl.formatMessage({ defaultMessage: 'Someone' });
      }
      return intl.formatMessage({
        defaultMessage: '{name} {data} on {display_name}',
      }, {
        name,
        data: getNotificationDataText(type, data),
        display_name: object.display_name,
      });
    }

    case 'ShareViewed':
      return intl.formatMessage({
        defaultMessage: 'Someone viewed a link you shared for the first time',
      });

    case 'ImportFinished': {
      if (object.status === 'failed') {
        return intl.formatMessage({
          defaultMessage: 'Your import from {name} has failed',
        }, { name: object.importer.name });
      }
      if (object.status === 'overlimit') {
        return intl.formatMessage({
          defaultMessage: 'Your import from {name} has failed',
        }, { name: object.importer.name });
      }
      return intl.formatMessage({
        defaultMessage: 'Your import from {name} is complete',
      }, { name: object.importer.name });
    }

    case 'UploadPageUpload':
      return intl.formatMessage({
        defaultMessage: '{name} uploaded {count} {count, plural, one {file} other {files}} to Collection {collection}',
      }, { name: user.name, count: data.files, collection: object.collection?.name });

    case 'Test':
      return intl.formatMessage({
        defaultMessage: 'This is a test desktop notification',
      });

    default:
      return '';
  }
};

const getBrowserNotificationUrl = (notification) => {
  const {
    type,
    object,
  } = notification;

  switch (type) {
    case 'FileFavorited':
    case 'ShareFileFavorited':
    case 'FileNewVersionUploaded':
      return `${window.location.origin}/files/${object.uuid}`;

    case 'CollectionFilesAdded':
    case 'CollectionAccessGranted':
      return `${window.location.origin}/collections/${object.uuid}`;

    case 'ShareViewed':
      return `${window.location.origin}/shares/${object.uuid}`;

    default:
      return window.location.origin;
  }
};

const composeBrowserNotification = (notification) => {
  const browserNotification = new Notification('Stockpress', {
    image: 'https://images.stockpress.co/stockpress-logo-notifications.png',
    icon: 'https://images.stockpress.co/stockpress-logo-notifications.png',
    body: getBrowserNotificationText(notification),
  });

  browserNotification.addEventListener('click', (e) => {
    e.preventDefault();
    window.open(getBrowserNotificationUrl(notification), '_blank');
  });

  browserNotification.addEventListener('show', () => {
    if (notification.sound) {
      playNotificationSound();
    }
  });
};

export const sendBrowserNotification = (notification) => {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    composeBrowserNotification(notification);
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission((permission) => {
      if (permission === 'granted') {
        composeBrowserNotification(notification);
      }
    });
  }

  return true;
};

export const shakeNotificationIcon = () => {
  window.$('.nav-notifications .fa-bell').addClass('fa-shake');
  setTimeout(() => {
    window.$('.nav-notifications .fa-bell').removeClass('fa-shake');
  }, 500);
};

export const updateNotificationNumberInPageTitle = (number) => {
  if (!window.sp.Badger) {
    window.sp.Badger = new Badger();
  }
  if (window.sp.titleNumber !== number) {
    window.sp.titleNumber = number;
    window.sp.Badger.value = number;
  }
};

const updatePresenceChannelStats = (channel) => {
  const pusherChannel = window.Echo.connector.channels[`presence-${channel}`];
  if (pusherChannel && pusherChannel.subscription) {
    const { members } = pusherChannel.subscription;
    if (members) {
      const store = storeProvider.getStore();
      store.dispatch(setOnlineUsers(Object.keys(members.members)));
    }
  }
};

export const initNotificationChannels = () => {
  const profile = getCurrentUser();
  if (!profile || !profile.uuid) {
    return;
  }

  const store = storeProvider.getStore();

  window.Echo.private(`notifications.${profile.uuid}`)
    .listen('NotificationSent', (e) => {
      store.dispatch(addNotification(e.notification));
      sendBrowserNotification(e.notification);
      playNotificationSound();
      shakeNotificationIcon();
    })
    .listen('BackendAction', (e) => {
      store.dispatch(setBackendAction(e));
    });

  if (profile.client && profile.client.uuid && !profile.is_superadmin) {
    const presenceChannel = `users.${profile.client.uuid}`;
    window.Echo.join(presenceChannel)
      .here(() => {
        updatePresenceChannelStats(presenceChannel);
      })
      .joining(() => {
        updatePresenceChannelStats(presenceChannel);
      })
      .leaving(() => {
        updatePresenceChannelStats(presenceChannel);
      })
      .error((error) => {
        Console.error(error);
      });
  }

  if (profile.client && profile.client.uuid) {
    window.Echo.private(`general.${profile.client.uuid}`)
      .listen('BackendAction', (e) => {
        if (e.id.indexOf('file_fragment_') > -1) {
          store.dispatch(updateFileFragment(e.data));
        }
      });
  }
};
